import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

interface ActivityTabObjectsProps {
  onItemDrop: (
    itemId: string,
    itemName: string,
    itemType: string,
    x: number,
    y: number
  ) => void;
}

interface DraggedItem {
  id: string;
  name: string;
  type: string;
}

const ActivityTabObjects: React.FC<ActivityTabObjectsProps> = ({
  onItemDrop,
}) => {
  const isTouchDevice =
    typeof window !== "undefined" && "ontouchstart" in window;

  // Refs to track dragging state
  const draggedItemRef = useRef<DraggedItem | null>(null);
  const draggingRef = useRef<boolean>(false);
  const isLongPressRef = useRef<boolean>(false);
  const touchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleTouchStart = (
    e: React.TouchEvent<HTMLElement>,
    itemName: string,
    itemType: string
  ) => {
    console.log(e);
    draggingRef.current = false;
    isLongPressRef.current = false;

    // Prepare a timeout for 200ms to detect a long press
    touchTimeoutRef.current = setTimeout(() => {
      isLongPressRef.current = true;
    }, 200);

    // Create a unique ID and store the item we might drag
    const uniqueId = uuidv4();
    draggedItemRef.current = {
      id: uniqueId,
      name: itemName,
      type: itemType,
    };
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLElement>) => {
    // If it's a long press, do nothing
    if (isLongPressRef.current) return;

    // If we haven't started dragging yet, this movement means "start drag"
    if (!draggingRef.current) {
      // Cancel the long-press timer
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
        touchTimeoutRef.current = null;
      }

      // Mark that we're officially dragging now
      draggingRef.current = true;

      // Create some visual feedback element
      const touch = e.touches[0];
      const chipElement = document.createElement("div");
      chipElement.id = "dragging-chip";
      chipElement.style.position = "fixed";
      chipElement.style.left = `${touch.clientX - 50}px`;
      chipElement.style.top = `${touch.clientY - 25}px`;
      chipElement.style.zIndex = "9999";
      chipElement.style.opacity = "0.8";
      chipElement.style.padding = "4px 8px";
      chipElement.style.backgroundColor = "#444";
      chipElement.style.color = "white";
      chipElement.textContent = draggedItemRef.current?.name ?? "Object";
      document.body.appendChild(chipElement);
    }

    // If we are already dragging, move the chip with the finger
    if (draggingRef.current) {
      // e.preventDefault();
      const touch = e.touches[0];
      const chip = document.getElementById("dragging-chip");
      if (chip) {
        chip.style.left = `${touch.clientX - 50}px`;
        chip.style.top = `${touch.clientY - 25}px`;
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLElement>) => {
    // Clear any pending long press timer
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
      touchTimeoutRef.current = null;
    }

    // Remove visual feedback
    const chip = document.getElementById("dragging-chip");
    if (chip) {
      document.body.removeChild(chip);
    }

    // If we actually dragged (short press + move), process the drop
    if (draggingRef.current && draggedItemRef.current) {
      const touch = e.changedTouches[0];
      onItemDrop(
        draggedItemRef.current.id,
        draggedItemRef.current.name,
        draggedItemRef.current.type,
        touch.clientX,
        touch.clientY
      );
    }

    // Reset flags
    draggingRef.current = false;
    isLongPressRef.current = false;
    draggedItemRef.current = null;
  };

  const handleTouchCancel = () => {
    // Clear any pending timer
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
      touchTimeoutRef.current = null;
    }

    // Remove visual chip
    const chip = document.getElementById("dragging-chip");
    if (chip) {
      document.body.removeChild(chip);
    }

    // Reset flags
    draggingRef.current = false;
    isLongPressRef.current = false;
    draggedItemRef.current = null;
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLElement>,
    itemName: string,
    itemType: string
  ) => {
    const uniqueId = uuidv4();
    e.dataTransfer.setData("item_id", uniqueId);
    e.dataTransfer.setData("item_name", itemName);
    e.dataTransfer.setData("item_type", itemType);

    const chipElement = e.currentTarget.cloneNode(true) as HTMLElement;
    chipElement.style.position = "absolute";
    chipElement.style.width = "50px";
    chipElement.style.height = "50px";
    chipElement.style.display = "flex";
    chipElement.style.alignItems = "center";
    chipElement.style.justifyContent = "center";
    chipElement.style.backgroundColor = "#444";
    chipElement.style.color = "white";
    chipElement.style.cursor = "grab";
    document.body.appendChild(chipElement);

    e.dataTransfer.setDragImage(chipElement, 25, 25);
  };

  return (
    <Box>
      <Typography
        variant="subtitle1"
        sx={{ marginBottom: "12px", fontWeight: "bold" }}
      >
        Objects
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "16px",
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <Box
          sx={{
            marginY: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            draggable={!isTouchDevice}
            onDragStart={(e) => handleDragStart(e, "Entrance", "entrances")}
            onTouchStart={(e) => handleTouchStart(e, "Entrance", "entrances")}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            sx={{
              width: "50px",
              height: "50px",
              cursor: "grab",
              backgroundImage: 'url("/entrance.svg")',
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Typography variant="body2" sx={{ marginTop: 1, fontWeight: "bold" }}>
            Entrance
          </Typography>
        </Box>

        <Box
          sx={{
            marginY: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            draggable={!isTouchDevice}
            onDragStart={(e) => handleDragStart(e, "Toilet Seat", "toiletSeat")}
            onTouchStart={(e) =>
              handleTouchStart(e, "Toilet Seat", "toiletSeat")
            }
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            sx={{
              width: "50px",
              height: "50px",
              cursor: "grab",
              backgroundImage: 'url("/toiletSeat.svg")',
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Typography variant="body2" sx={{ marginTop: 1, fontWeight: "bold" }}>
            Toilet Seats
          </Typography>
        </Box>

        <Box
          sx={{
            marginY: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            draggable={!isTouchDevice}
            onDragStart={(e) =>
              handleDragStart(e, "Kitchen Burner Slab", "kitchenBurnerSlab")
            }
            onTouchStart={(e) =>
              handleTouchStart(e, "Kitchen Burner Slab", "kitchenBurnerSlab")
            }
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            sx={{
              width: "50px",
              height: "50px",
              cursor: "grab",
              backgroundImage: 'url("/kitchenBurner.svg")',
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Typography variant="body1" sx={{ marginTop: 1, fontWeight: "bold" }}>
            Kitchen Burner
          </Typography>
        </Box>

        {/* House Pillars */}
        <Box
          sx={{
            marginY: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            draggable={!isTouchDevice}
            onDragStart={(e) =>
              handleDragStart(e, "House Pillar", "housePillar")
            }
            onTouchStart={(e) =>
              handleTouchStart(e, "House Pillar", "housePillar")
            }
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            sx={{
              width: "50px",
              height: "50px",
              cursor: "grab",
              backgroundImage: 'url("/pillar.svg")',
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Typography variant="body2" sx={{ marginTop: 1, fontWeight: "bold" }}>
            House Pillars
          </Typography>
        </Box>

        <Box
          sx={{
            marginY: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            draggable={!isTouchDevice}
            onDragStart={(e) => handleDragStart(e, "Wall", "wall")}
            onTouchStart={(e) => handleTouchStart(e, "Wall", "wall")}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            sx={{
              width: "50px",
              height: "50px",
              cursor: "grab",
              backgroundImage: 'url("/wall.svg")',
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <Typography variant="body2" sx={{ marginTop: 1, fontWeight: "bold" }}>
            Walls
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ActivityTabObjects;
